import type {
  ComponentUsageRow,
  TenantListQuery,
  TenantListResponse,
  TenantOverviewRow,
  TenantUserListQuery,
  TenantUserListResponse,
} from "@repo/shared/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "@/lib/api/_client";
import {
  checkFeatureFlag,
  getComponentUsage,
  getTenantDetail,
  getTenantsOverview,
  updateTenantApiRateLimit,
} from "@/lib/api/admin";
import { toQueryString } from "@/lib/api/query-string";
import { TENANT_USERS_KEY, useMe } from "./use-clients";

// ─── Types ──────────────────────────────────────────

export type PartnerInfo = {
  id: string;
  name: string;
};

// ─── Feature flags ──────────────────────────────────

export function useFeatureFlag(flagKey: string) {
  const { data, isLoading } = useQuery({
    queryKey: ["feature-flag", flagKey],
    queryFn: () => checkFeatureFlag(flagKey),
  });
  return { enabled: data?.enabled ?? false, isLoading };
}

// ─── Admin access ───────────────────────────────────

/**
 * Who may open the cross-tenant admin pages: an `admin` user of the quantfolio
 * tenant or of a partner tenant. Mirrors the API gates in middleware/permissions.ts
 * (requireQuantfolio / createRequireQuantfolioOrPartner) — UX only, the API enforces it.
 */
export function useAdminAccess() {
  const { me, isLoading: meLoading } = useMe();
  const { enabled: isPartner, isLoading: flagLoading } = useFeatureFlag("partner");

  const isAdmin = me?.permissions?.includes("admin") ?? false;
  const isQuantfolio = me?.tenantSlug === "quantfolio";
  return {
    isLoading: meLoading || flagLoading,
    isQuantfolio,
    isPartner,
    /** Admin of the quantfolio tenant — the quantfolio-only pages. */
    isQuantfolioAdmin: isAdmin && isQuantfolio,
    /** Admin of the quantfolio tenant or of a partner tenant. */
    canViewAdmin: isAdmin && (isQuantfolio || isPartner),
  };
}

// ─── Tenant detail ──────────────────────────────────

export function useTenantDetail(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin", "tenants", id, "detail"],
    queryFn: () => getTenantDetail(id!),
    enabled: !!id,
  });
  return { tenant: data ?? null, error, isLoading };
}

/** QL-359 — set or clear (`null`) a tenant's API rate limit override. */
export function useUpdateTenantApiRateLimit(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limit: number | null) => updateTenantApiRateLimit(tenantId, { limit }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "detail"] }),
  });
}

// ─── Tenants overview ──────────────────────────────

export function useTenantsOverview() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin", "tenants", "overview"],
    queryFn: getTenantsOverview,
  });
  return { tenants: (data ?? []) as TenantOverviewRow[], error, isLoading };
}

// ─── Component usage ───────────────────────────────

export function useComponentUsage() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin", "component-usage"],
    queryFn: getComponentUsage,
  });
  return { rows: (data?.rows ?? []) as ComponentUsageRow[], error, isLoading };
}

// ─── Admin hooks ──────────────────────────────────

/** The paging facts every paged admin list hook hands back next to its rows. */
export type AdminListPagination = {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

const EMPTY_PAGINATION: AdminListPagination = {
  page: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 1,
};

function paginationOf(data: AdminListPagination): AdminListPagination {
  return {
    page: data.page,
    pageSize: data.pageSize,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
  };
}

/** QL-136 — one page of tenants for the Quantfolio admin; filter and search run on the server. */
export function useAdminTenants(query: TenantListQuery = {}) {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "tenants", "list", query],
    queryFn: () =>
      get<TenantListResponse>(
        `/internal/admin/tenants${toQueryString(query satisfies TenantListQuery)}`,
      ),
    placeholderData: keepPreviousData,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
  return {
    tenants: data?.items ?? [],
    pagination: data ? paginationOf(data) : EMPTY_PAGINATION,
    error,
    isLoading,
    isFetching,
    mutate,
  };
}

export function usePartnersList() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin", "tenants", "partners"],
    queryFn: () => get<PartnerInfo[]>("/internal/admin/tenants/partners"),
  });
  return { partners: data ?? [], error, isLoading };
}

/** QL-136 — one page of the tenants the caller's partner tenant owns. */
export function usePartnerTenants(query: TenantListQuery = {}) {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "tenants", "partner-tenants", query],
    queryFn: () =>
      get<TenantListResponse>(
        `/internal/admin/tenants/partner-tenants${toQueryString(query satisfies TenantListQuery)}`,
      ),
    placeholderData: keepPreviousData,
  });
  const queryClient = useQueryClient();
  const mutate = () => queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
  return {
    tenants: data?.items ?? [],
    pagination: data ? paginationOf(data) : EMPTY_PAGINATION,
    error,
    isLoading,
    isFetching,
    mutate,
  };
}

/** QL-136 — one page of a tenant's users, for the admin tenant detail. */
export function useAdminTenantUsers(tenantId: string | null, query: TenantUserListQuery = {}) {
  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "tenants", tenantId, "users", query],
    queryFn: () =>
      get<TenantUserListResponse>(
        `/internal/admin/tenants/${tenantId}/users${toQueryString(query satisfies TenantUserListQuery)}`,
      ),
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
  });
  const queryClient = useQueryClient();
  const mutate = () => {
    queryClient.invalidateQueries({
      queryKey: ["admin", "tenants", tenantId, "users"],
    });
    // The tenant may be the admin's own: its owner/member pickers read these (QL-380).
    queryClient.invalidateQueries({ queryKey: TENANT_USERS_KEY });
  };
  return {
    users: data?.items ?? [],
    pagination: data ? paginationOf(data) : EMPTY_PAGINATION,
    error,
    isLoading,
    isFetching,
    mutate,
  };
}
